import { format } from "date-fns";
import { Bar, Doughnut, Line } from "react-chartjs-2";

import { ensureIsDate, getColor } from "@/utils";

import { BaseWidget } from "./BaseWidget";
import {
  barOptions,
  eventPropsForPie,
  eventPropsForTimeline,
  lineOptions,
  pieOptions,
  setOptionFromDefault,
} from "./chart_options";
import { CountText } from "./CountText";
import { showOneDecimalPlace, sumArray } from "./helpers";

// Example: 2022-01-01
const monthWithFirstOfMonth = /[0-9]{4}-[0-9]{2}-01/;

const hasData = (data) =>
  data &&
  data.map((d) => (d.statsPerDateAndSource || []).length).reduce(sumArray, 0) >
    0;

const NumMentionsLineWidget = (props) => {
  const isStacked = props.chartType == "area_stacked";
  const KEY = "statsPerDateAndSource";

  const formatLabel = (label) =>
    label.match(monthWithFirstOfMonth)
      ? format(ensureIsDate(label), "yyyy-MM")
      : label;

  // `data` is passed in rather than read off `props` inside these closures:
  // React Compiler memoizes a closure on the property paths it captures, and it
  // dereferences those paths on every render — so a captured `props.data[0]`
  // would throw before the `hasData` guard below could keep us out of it, and
  // `props.data` is null until the widget's data has loaded.
  const getLabels = (data) =>
    (data[0][KEY] || []).map((i) => formatLabel(i[0]));

  const getDatasets = (data) =>
    data.map((chartItem, idx) => {
      const totals = (chartItem[KEY] || []).map((item) => {
        return Object.values(item[1]).reduce(sumArray, 0);
      });

      return {
        data: totals,
        label: chartItem.meta.title,
        ...setOptionFromDefault(idx, isStacked, {
          title: chartItem.meta.title,
          scope: "source",
        }),

        // store source type and id for MentionModal
        source: { id: chartItem.meta.id, type: chartItem.meta.type },
      };
    });

  const widgetHasData = hasData(props.data);

  const chartData = {
    labels: widgetHasData ? getLabels(props.data) : [],
    datasets: widgetHasData ? getDatasets(props.data) : [],
  };

  return (
    <BaseWidget {...props} hasData={widgetHasData}>
      {props.chartType === "bar" ? (
        <Bar
          data={chartData}
          options={{
            ...barOptions(true, props.size),
            ...eventPropsForTimeline(chartData, props),
          }}
        />
      ) : (
        <Line
          data={chartData}
          options={{
            ...lineOptions(isStacked, props.size),
            ...eventPropsForTimeline(chartData, props),
          }}
        />
      )}
    </BaseWidget>
  );
};

const NumMentionsBarWidget = NumMentionsLineWidget;

const NumMentionsPieWidget = (props) => {
  const KEY = "statsPerDateAndSource";

  let datasets = [],
    labels = [],
    backgroundColors = [],
    sum = 0,
    sources = [];

  if (props.data) {
    props.data.forEach((entry, idx) => {
      const totals = (entry[KEY] || []).map((item) => {
        return Object.values(item[1]).reduce(sumArray, 0);
      });
      const value = totals.reduce(sumArray, 0);
      sum += value;
      datasets.push(value);
      labels.push(entry.meta.title);
      backgroundColors.push(
        getColor(idx, { title: entry.meta.title, scope: "source" })
      );

      // store source type and id for MentionModal
      sources.push({ id: entry.meta.id, type: entry.meta.type });
    });

    // override datasets with % version
    datasets = datasets.map(
      (value, idx) => (datasets[idx] = showOneDecimalPlace(value, sum))
    );

    datasets = [
      {
        data: datasets,
        borderWidth: 0,
        backgroundColor: backgroundColors,
        hoverBackgroundColor: backgroundColors,
        sources: sources,
      },
    ];
  }

  const chartData = {
    labels,
    datasets,
  };

  const options = pieOptions();

  return (
    <BaseWidget {...props} hasData={hasData(props.data)}>
      <Doughnut
        data={chartData}
        options={{ ...options, ...eventPropsForPie(chartData, props) }}
      />
    </BaseWidget>
  );
};

const NumMentionsSumWidget = (props) => {
  const KEY = "statsPerDateAndSource";

  // basically sum all mentions in all sources
  const sum = props.data
    ? props.data
        .map((entry) =>
          (entry[KEY] || [])
            .map((item) => Object.values(item[1]).reduce(sumArray, 0))
            .reduce(sumArray, 0)
        )
        .reduce(sumArray, 0)
    : 0;

  return (
    <BaseWidget {...props} hasData={hasData(props.data)}>
      <CountText count={sum} {...props} />
    </BaseWidget>
  );
};

const NumMentionsWidget = (props) => {
  return props.chartType == "pie" ? (
    <NumMentionsPieWidget {...props} />
  ) : props.chartType == "bar" ? (
    <NumMentionsBarWidget {...props} />
  ) : props.chartType == "sum" ? (
    <NumMentionsSumWidget {...props} />
  ) : (
    <NumMentionsLineWidget {...props} />
  );
};

export { NumMentionsWidget };
